import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Checkbox, Input } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function INTFCPlacementChecklist() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const toggleTaskCompleted = (e) => {
    const obj = { ...data };
    const fieldName = e.currentTarget.name.replace("_cb", "");

    if (obj[fieldName]["@value"] !== undefined) {
      obj[fieldName]["@value"] = "";
    } else {
      obj[fieldName]["@value"] = moment().format(moment.HTML5_FMT.DATE);
    }

    setData(obj);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Input
                name="SAFYWorker"
                label="SAFY Worker"
                error={errors.SAFYWorker}
                value={getFieldValue(data.SAFYWorker)}
                onChange={handleChange}
                identical={compare("SAFYWorker")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                className="mb-3"
              />
              <div className="container border mb-3">
                <div className="row border-bottom">
                  <div className="col py-2 bg-light text-center">
                    <b>INTAKE</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end"></div>
                  <div className="col-3 py-2 border-end">
                    <b>Date Completed</b>
                  </div>
                  <div className="col py-2">
                    <b>Task</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="ReferralSigned_cb"
                      checked={
                        data.ReferralSigned["@value"] !== undefined &&
                        data.ReferralSigned["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="ReferralSigned"
                      error={errors.ReferralSigned}
                      value={getFieldValue(data.ReferralSigned)}
                      onChange={handleChange}
                      identical={compare("ReferralSigned")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Referral must be signed by Foster Parents
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="InitialCarePlan_cb"
                      checked={
                        data.InitialCarePlan["@value"] !== undefined &&
                        data.InitialCarePlan["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="InitialCarePlan"
                      error={errors.InitialCarePlan}
                      value={getFieldValue(data.InitialCarePlan)}
                      onChange={handleChange}
                      identical={compare("InitialCarePlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    7-Day Care Plan/Initial Case Note (done at first meeting)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="CircleOfSupport_cb"
                      checked={
                        data.CircleOfSupport["@value"] !== undefined &&
                        data.CircleOfSupport["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="CircleOfSupport"
                      error={errors.CircleOfSupport}
                      value={getFieldValue(data.CircleOfSupport)}
                      onChange={handleChange}
                      identical={compare("CircleOfSupport")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">Circle of Support</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="FosterHomeDrillReport_cb"
                      checked={
                        data.FosterHomeDrillReport["@value"] !== undefined &&
                        data.FosterHomeDrillReport["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="FosterHomeDrillReport"
                      error={errors.FosterHomeDrillReport}
                      value={getFieldValue(data.FosterHomeDrillReport)}
                      onChange={handleChange}
                      identical={compare("FosterHomeDrillReport")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Foster Home Fire Tornado Drill Report - with each new
                    placement
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="GiveFosterParents_cb"
                      checked={
                        data.GiveFosterParents["@value"] !== undefined &&
                        data.GiveFosterParents["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="GiveFosterParents"
                      error={errors.GiveFosterParents}
                      value={getFieldValue(data.GiveFosterParents)}
                      onChange={handleChange}
                      identical={compare("GiveFosterParents")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Give Foster Parents:
                    <ul>
                      <li>F-003 Daily Med Log - get from FP every month</li>
                      <li>Clothing Inventory</li>
                    </ul>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="UploadPicture_cb"
                      checked={
                        data.UploadPicture["@value"] !== undefined &&
                        data.UploadPicture["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="UploadPicture"
                      error={errors.UploadPicture}
                      value={getFieldValue(data.UploadPicture)}
                      onChange={handleChange}
                      identical={compare("UploadPicture")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Upload Picture into SOAR Profile
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SendFCMROI_cb"
                      checked={
                        data.SendFCMROI["@value"] !== undefined &&
                        data.SendFCMROI["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="SendFCMROI"
                      error={errors.SendFCMROI}
                      value={getFieldValue(data.SendFCMROI)}
                      onChange={handleChange}
                      identical={compare("SendFCMROI")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Send FCM Releases of Information via DocuSign
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="CompletePSD_cb"
                      checked={
                        data.CompletePSD["@value"] !== undefined &&
                        data.CompletePSD["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="CompletePSD"
                      error={errors.CompletePSD}
                      value={getFieldValue(data.CompletePSD)}
                      onChange={handleChange}
                      identical={compare("CompletePSD")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Complete the PSD/DAF within 30 days
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DevelopTreatmentPlan_cb"
                      checked={
                        data.DevelopTreatmentPlan["@value"] !== undefined &&
                        data.DevelopTreatmentPlan["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="DevelopTreatmentPlan"
                      error={errors.DevelopTreatmentPlan}
                      value={getFieldValue(data.DevelopTreatmentPlan)}
                      onChange={handleChange}
                      identical={compare("DevelopTreatmentPlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Develop the 30-day treatment plan
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="CompleteLifeSkills_cb"
                      checked={
                        data.CompleteLifeSkills["@value"] !== undefined &&
                        data.CompleteLifeSkills["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="CompleteLifeSkills"
                      error={errors.CompleteLifeSkills}
                      value={getFieldValue(data.CompleteLifeSkills)}
                      onChange={handleChange}
                      identical={compare("CompleteLifeSkills")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Complete the Casey Life Skills (CLSA) for Independent Living
                    Clients (16 +)
                    <br />
                    <i>
                      (If youth is cognitively impaired, complete the DD Daily
                      Living Skills Assessment F-176 instead)
                    </i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="ILPlan_cb"
                      checked={
                        data.ILPlan["@value"] !== undefined &&
                        data.ILPlan["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="ILPlan"
                      error={errors.ILPlan}
                      value={getFieldValue(data.ILPlan)}
                      onChange={handleChange}
                      identical={compare("ILPlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    IL Plan added to care plan (16+)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="PrimaryFamily_cb"
                      checked={
                        data.PrimaryFamily["@value"] !== undefined &&
                        data.PrimaryFamily["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="PrimaryFamily"
                      error={errors.PrimaryFamily}
                      value={getFieldValue(data.PrimaryFamily)}
                      onChange={handleChange}
                      identical={compare("PrimaryFamily")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Primary Family Contact - with approval from the custodian
                    and to occur within the first 30 days and documented in a
                    case note
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="IARCA_cb"
                      checked={
                        data.IARCA["@value"] !== undefined &&
                        data.IARCA["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="IARCA"
                      error={errors.IARCA}
                      value={getFieldValue(data.IARCA)}
                      onChange={handleChange}
                      identical={compare("IARCA")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    IARCA - Completed in EON, scanned into SOAR
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="SendIntakePacket_cb"
                      checked={
                        data.SendIntakePacket["@value"] !== undefined &&
                        data.SendIntakePacket["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="SendIntakePacket"
                      error={errors.SendIntakePacket}
                      value={getFieldValue(data.SendIntakePacket)}
                      onChange={handleChange}
                      identical={compare("SendIntakePacket")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    DOM will send the Intake Packet to an FCM requesting:
                    <li>F-515 Foster Care Consent and Authorizations</li>
                    <li>DCS Authorization for Healthcare SF54247</li>
                    <li>CANS</li>
                    <li>Birth Certificate</li>
                    <li>Medicaid/Insurance Card</li>
                    <li>Medical Passport and Immunization Records</li>
                    <li>School Records/Current IEP</li>
                    <li>Custody Order/Court Orders</li>
                    <li>Current County Case Plan</li>
                    <li>ICPA (ICPR)</li>
                    <li>
                      Completed Authorization for Psychotropic Medication
                      SF53545, if applicable
                    </li>
                    <i>
                      Note: the DOM sends this to the FCM one time at the time
                      of placement. If not received, the CM must request at
                      least 2 additional times if items are not received in the
                      first 30 days. You do not need to document additional
                      requests.
                    </i>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="Physical_cb"
                      checked={
                        data.Physical["@value"] !== undefined &&
                        data.Physical["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Physical"
                      error={errors.Physical}
                      value={getFieldValue(data.Physical)}
                      onChange={handleChange}
                      identical={compare("Physical")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Physical</b>: Within 10 days of placement and occur
                    within 30 days of placement and should be in EPSDT Format
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="Dental_cb"
                      checked={
                        data.Dental["@value"] !== undefined &&
                        data.Dental["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Dental"
                      error={errors.Dental}
                      value={getFieldValue(data.Dental)}
                      onChange={handleChange}
                      identical={compare("Dental")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Dental</b>: Within 30 days of placement (Ages 2 and
                    older)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="Hearing_cb"
                      checked={
                        data.Hearing["@value"] !== undefined &&
                        data.Hearing["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Hearing"
                      error={errors.Hearing}
                      value={getFieldValue(data.Hearing)}
                      onChange={handleChange}
                      identical={compare("Hearing")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Hearing</b>: Within 30 days of placement (included in
                    EPSDT exam or can use school hearing screen)
                  </div>
                </div>
                <div className="row">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="Vision_cb"
                      checked={
                        data.Vision["@value"] !== undefined &&
                        data.Vision["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="Vision"
                      error={errors.Vision}
                      value={getFieldValue(data.Vision)}
                      onChange={handleChange}
                      identical={compare("Vision")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Vision</b>: Within 30 days of placement (included in
                    EPSDT exam or can use school vision screen)
                  </div>
                </div>
              </div>
              <div className="container border mb-3">
                <div className="row border-bottom">
                  <div className="col py-2 bg-light text-center">
                    <b>ONGOING</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end"></div>
                  <div className="col-3 py-2 border-end">
                    <b>Date Completed</b>
                  </div>
                  <div className="col py-2">
                    <b>Task</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="UpdateFCM_cb"
                      checked={
                        data.UpdateFCM["@value"] !== undefined &&
                        data.UpdateFCM["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="UpdateFCM"
                      error={errors.UpdateFCM}
                      value={getFieldValue(data.UpdateFCM)}
                      onChange={handleChange}
                      identical={compare("UpdateFCM")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>At least 2x/month</b>: Case notes updating FCM on youth
                    progress
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="MonthlyReport_cb"
                      checked={
                        data.MonthlyReport["@value"] !== undefined &&
                        data.MonthlyReport["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="MonthlyReport"
                      error={errors.MonthlyReport}
                      value={getFieldValue(data.MonthlyReport)}
                      onChange={handleChange}
                      identical={compare("MonthlyReport")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Every Month</b>: Complete a Monthly Report in the SAFY
                    Portal (by the 10th)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="UpdatePrimaryFamily_cb"
                      checked={
                        data.UpdatePrimaryFamily["@value"] !== undefined &&
                        data.UpdatePrimaryFamily["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="UpdatePrimaryFamily"
                      error={errors.UpdatePrimaryFamily}
                      value={getFieldValue(data.UpdatePrimaryFamily)}
                      onChange={handleChange}
                      identical={compare("UpdatePrimaryFamily")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Every Month</b>: Attempt to contact and update primary
                    family (document in case notes and monthly reports)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="GetFromFPS_cb"
                      checked={
                        data.GetFromFPS["@value"] !== undefined &&
                        data.GetFromFPS["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="GetFromFPS"
                      error={errors.GetFromFPS}
                      value={getFieldValue(data.GetFromFPS)}
                      onChange={handleChange}
                      identical={compare("GetFromFPS")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Every Month</b>: Get from FPS the following
                    <ul>
                      <li>F-003 Daily Medication Log</li>
                      <li>F-179 Foster Home Fire Tornado Drill Report</li>
                    </ul>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="UpdateCarePlan_cb"
                      checked={
                        data.UpdateCarePlan["@value"] !== undefined &&
                        data.UpdateCarePlan["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="UpdateCarePlan"
                      error={errors.UpdateCarePlan}
                      value={getFieldValue(data.UpdateCarePlan)}
                      onChange={handleChange}
                      identical={compare("UpdateCarePlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Every 90 days</b>: Update the Care plan
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DentalScreening_cb"
                      checked={
                        data.DentalScreening["@value"] !== undefined &&
                        data.DentalScreening["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="DentalScreening"
                      error={errors.DentalScreening}
                      value={getFieldValue(data.DentalScreening)}
                      onChange={handleChange}
                      identical={compare("DentalScreening")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Every 6 months</b>: Dental Screening (beginning age 2)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="AnnualTasks_cb"
                      checked={
                        data.AnnualTasks["@value"] !== undefined &&
                        data.AnnualTasks["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="AnnualTasks"
                      error={errors.AnnualTasks}
                      value={getFieldValue(data.AnnualTasks)}
                      onChange={handleChange}
                      identical={compare("AnnualTasks")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>Annually</b>:
                    <ul>
                      <li>
                        Complete a Casey Life Skills Assessment (Youth 16+)
                      </li>
                      <li>Update ROIs</li>
                      <li>Physical Exam</li>
                      <li>
                        Hearing Exam (for corrected hearing or tubes or as
                        recommended by child's physician)
                      </li>
                      <li>
                        Vision Screening (for corrected vision-
                        glasses/contacts)
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="PsychotropicAuthorization_cb"
                      checked={
                        data.PsychotropicAuthorization["@value"] !==
                          undefined &&
                        data.PsychotropicAuthorization["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="PsychotropicAuthorization"
                      error={errors.PsychotropicAuthorization}
                      value={getFieldValue(data.PsychotropicAuthorization)}
                      onChange={handleChange}
                      identical={compare("PsychotropicAuthorization")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>As needed</b>: Get an Authorization for Psychotropic
                    Medication when meds change or child begins a psychotropic
                    medication
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DCSTreatmentAuthorization_cb"
                      checked={
                        data.DCSTreatmentAuthorization["@value"] !==
                          undefined &&
                        data.DCSTreatmentAuthorization["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="DCSTreatmentAuthorization"
                      error={errors.DCSTreatmentAuthorization}
                      value={getFieldValue(data.DCSTreatmentAuthorization)}
                      onChange={handleChange}
                      identical={compare("DCSTreatmentAuthorization")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>As needed</b>: Get an Authorization for treatment from
                    DCS prior to any non-routine, non-emergency care and mental
                    health treatment (in case note and monthly report)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="IncidentReports_cb"
                      checked={
                        data.IncidentReports["@value"] !== undefined &&
                        data.IncidentReports["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="IncidentReports"
                      error={errors.IncidentReports}
                      value={getFieldValue(data.IncidentReports)}
                      onChange={handleChange}
                      identical={compare("IncidentReports")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>As needed</b>: Incident Reports - within 24 hours to
                    notify DCS of serious issue related to health,
                    case/permanency plan, welfare, and general well-being
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="RespiteProviderPack_cb"
                      checked={
                        data.RespiteProviderPack["@value"] !== undefined &&
                        data.RespiteProviderPack["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="RespiteProviderPack"
                      error={errors.RespiteProviderPack}
                      value={getFieldValue(data.RespiteProviderPack)}
                      onChange={handleChange}
                      identical={compare("RespiteProviderPack")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>As needed</b>: If youth is going to respite, send Respite
                    Provider Packet to FPs providing respite
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="RecordsUpToDate_cb"
                      checked={
                        data.RecordsUpToDate["@value"] !== undefined &&
                        data.RecordsUpToDate["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="RecordsUpToDate"
                      error={errors.RecordsUpToDate}
                      value={getFieldValue(data.RecordsUpToDate)}
                      onChange={handleChange}
                      identical={compare("RecordsUpToDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Keep the following records up to date:
                    <ul>
                      <li>Immunization Records</li>
                      <li>Report Cards/IEP</li>
                      <li>DCS Case Plan</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2 bg-light text-center">
                    <b>DISCHARGE</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end"></div>
                  <div className="col-3 py-2 border-end">
                    <b>Date Completed</b>
                  </div>
                  <div className="col py-2">
                    <b>Task</b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="GiveNotice_cb"
                      checked={
                        data.GiveNotice["@value"] !== undefined &&
                        data.GiveNotice["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="GiveNotice"
                      error={errors.GiveNotice}
                      value={getFieldValue(data.GiveNotice)}
                      onChange={handleChange}
                      identical={compare("GiveNotice")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Give 30-day notice to DCS prior to discharge (in writing)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="CompleteDischargePlan_cb"
                      checked={
                        data.CompleteDischargePlan["@value"] !== undefined &&
                        data.CompleteDischargePlan["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="CompleteDischargePlan"
                      error={errors.CompleteDischargePlan}
                      value={getFieldValue(data.CompleteDischargePlan)}
                      onChange={handleChange}
                      identical={compare("CompleteDischargePlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Complete a Discharge service plan which includes discharge
                    summary
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="IARCADischarge_cb"
                      checked={
                        data.IARCADischarge["@value"] !== undefined &&
                        data.IARCADischarge["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="IARCADischarge"
                      error={errors.IARCADischarge}
                      value={getFieldValue(data.IARCADischarge)}
                      onChange={handleChange}
                      identical={compare("IARCADischarge")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    IARCA Discharge (not if less than 30 days)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="MedicalPassport_cb"
                      checked={
                        data.MedicalPassport["@value"] !== undefined &&
                        data.MedicalPassport["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="MedicalPassport"
                      error={errors.MedicalPassport}
                      value={getFieldValue(data.MedicalPassport)}
                      onChange={handleChange}
                      identical={compare("MedicalPassport")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Send FCM a copy of up-to-date Medical Passport
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DischargeLifeSkills_cb"
                      checked={
                        data.DischargeLifeSkills["@value"] !== undefined &&
                        data.DischargeLifeSkills["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="DischargeLifeSkills"
                      error={errors.DischargeLifeSkills}
                      value={getFieldValue(data.DischargeLifeSkills)}
                      onChange={handleChange}
                      identical={compare("DischargeLifeSkills")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Complete a Discharge Casey Life Skills Assessment (CLSA) if
                    youth is 16 or older (not if less than 30 days)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="DischargeCircleOfSupport_cb"
                      checked={
                        data.DischargeCircleOfSupport["@value"] !== undefined &&
                        data.DischargeCircleOfSupport["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="DischargeCircleOfSupport"
                      error={errors.DischargeCircleOfSupport}
                      value={getFieldValue(data.DischargeCircleOfSupport)}
                      onChange={handleChange}
                      identical={compare("DischargeCircleOfSupport")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    Complete a Discharge Circle of Support Tool
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    <Checkbox
                      name="GetDischargeFromFP_cb"
                      checked={
                        data.GetDischargeFromFP["@value"] !== undefined &&
                        data.GetDischargeFromFP["@value"] !== ""
                      }
                      onChange={toggleTaskCompleted}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col-3 py-2 border-end">
                    <Input
                      name="GetDischargeFromFP"
                      error={errors.GetDischargeFromFP}
                      value={getFieldValue(data.GetDischargeFromFP)}
                      onChange={handleChange}
                      identical={compare("GetDischargeFromFP")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col py-2">
                    <b>FP to provide at discharge</b>:
                    <ul>
                      <li>Daily Medication Log - F-003</li>
                      <li>Inventory of Personal Items SF#54315</li>
                      <li>Medical Passport sent with youth</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default INTFCPlacementChecklist;
